import { useLayoutEffect } from 'react';
import type { RefObject } from 'react';

/**
 * Keeps a textarea as tall as its content.
 *
 * The measurement is two-step and the order is load-bearing: the height is
 * reset to `auto` first so the box can SHRINK when text is deleted. Measuring
 * without that reset makes the control grow monotonically — the classic
 * auto-growing-textarea bug.
 *
 * Listens on the element's own `input` event rather than depending on `value`.
 * A controlled-only effect would silently do nothing for an uncontrolled
 * textarea, and callers should not have to know which one they are writing.
 */
export function useAutoResize(
  ref: RefObject<HTMLTextAreaElement | null>,
  enabled: boolean,
): void {
  useLayoutEffect(() => {
    const element = ref.current;
    if (!enabled || !element) return;

    const resize = (): void => {
      element.style.height = 'auto';
      element.style.height = `${element.scrollHeight}px`;
    };

    resize();
    element.addEventListener('input', resize);
    return () => element.removeEventListener('input', resize);
  }, [ref, enabled]);
}